import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import { Shield, ShieldAlert, Calendar, Users, BookOpen, BarChart2, Video, Megaphone, Hash, Target, Footprints } from 'lucide-react';
import { Input } from './ui/input';

// American Football Positions
const POSITIONS = {
    Offense: ['QB', 'HB', 'FB', 'WR', 'TE', 'C', 'OG', 'OT'],
    Defense: ['DT', 'DE', 'MLB', 'OLB', 'CB', 'S'],
    Special: ['K', 'P']
};

const PERMISSIONS_CONFIG = [
    { id: 'manage_playbook', label: 'Playbook Management', icon: BookOpen, description: 'Add, edit, or delete plays and playbook files' },
    { id: 'manage_training_sessions', label: 'Sessions & Video Upload', icon: Video, description: 'Create training sessions and upload video clips' },
    { id: 'manage_calendar', label: 'Calendar Management', icon: Calendar, description: 'Create and edit team events in the calendar' },
    { id: 'edit_clip_details', label: 'Clip Details Editing', icon: Shield, description: 'Edit title and play info (down, distance, etc.) for video clips' },
    { id: 'enter_player_stats', label: 'Player Stat Entry', icon: BarChart2, description: 'Enter and manage individual player statistics' },
    { id: 'manage_announcements', label: 'Create Announcements', icon: Megaphone, description: 'Create and manage team announcements' },
];

const PermissionsModal = ({ open, onOpenChange, member, onSave, teamId, requestMode = false }) => {
    const [permissions, setPermissions] = useState([]);
    const [isCaptain, setIsCaptain] = useState(false);
    const [jerseyNumber, setJerseyNumber] = useState('');
    const [selectedPositions, setSelectedPositions] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (member) {
            setPermissions(member.permissions || []);
            setIsCaptain(member.is_captain || false);
            setJerseyNumber(member.jersey_number || '');
            setSelectedPositions(member.positions || []);
        }
    }, [member]);

    const togglePosition = (pos) => {
        if (selectedPositions.includes(pos)) {
            setSelectedPositions(selectedPositions.filter(p => p !== pos));
        } else {
            if (selectedPositions.length >= 3) return; // Limit to 3 (or remove limit for coach?)
            setSelectedPositions([...selectedPositions, pos]);
        }
    };

    const handlePermissionChange = (permId, checked) => {
        if (checked) {
            setPermissions([...permissions, permId]);
        } else {
            setPermissions(permissions.filter(p => p !== permId));
        }
    };

    const handleSave = async () => {
        try {
            setLoading(true);
            if (requestMode) {
                // In request mode, we only send positions
                await onSave(selectedPositions);
            } else {
                await onSave(member.user_id, {
                    permissions: permissions,
                    is_captain: isCaptain,
                    jersey_number: jerseyNumber,
                    positions: selectedPositions
                });
            }
            onOpenChange(false);
        } catch (error) {
            console.error('Failed to save permissions:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="bg-[#1a1a1b] border-gray-800 text-white sm:max-w-[425px]">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Shield className="w-5 h-5 text-blue-500" />
                        {requestMode ? 'Request Position Change' : `Manage Permissions: ${member?.username}`}
                    </DialogTitle>
                </DialogHeader>

                <div className="py-4 space-y-6">
                    {/* Jersey Number Input */}
                    {!requestMode && (
                        <div className="space-y-2">
                            <Label htmlFor="jersey_number" className="text-gray-400 flex items-center gap-2">
                                <Hash className="w-4 h-4" />
                                Jersey Number (Forma Numarası)
                            </Label>
                            <Input
                                id="jersey_number"
                                type="text"
                                value={jerseyNumber}
                                onChange={(e) => setJerseyNumber(e.target.value)}
                                placeholder="e.g. 12"
                                className="bg-[#0a0a0b] border-gray-700 text-white placeholder:text-gray-500"
                            />
                        </div>
                    )}

                    {/* Position Selection */}
                    <div className="space-y-3">
                        <Label className="text-gray-400 flex items-center gap-2">
                            <Target className="w-4 h-4" />
                            Positions (Mevki)
                        </Label>

                        <div className="space-y-3 p-3 bg-black/20 rounded-lg border border-gray-800">
                            {/* Offense */}
                            <div>
                                <h5 className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wider">Offense</h5>
                                <div className="flex flex-wrap gap-1.5">
                                    {POSITIONS.Offense.map(pos => (
                                        <button
                                            key={pos}
                                            onClick={() => togglePosition(pos)}
                                            className={`px-2 py-1 rounded text-xs font-medium border transition-colors ${selectedPositions.includes(pos)
                                                ? 'bg-green-900/40 border-green-500/50 text-green-400'
                                                : 'bg-[#0a0a0b] border-gray-700 text-gray-400 hover:border-gray-600'
                                                }`}
                                        >
                                            {pos}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Defense */}
                            <div>
                                <h5 className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wider">Defense</h5>
                                <div className="flex flex-wrap gap-1.5">
                                    {POSITIONS.Defense.map(pos => (
                                        <button
                                            key={pos}
                                            onClick={() => togglePosition(pos)}
                                            className={`px-2 py-1 rounded text-xs font-medium border transition-colors ${selectedPositions.includes(pos)
                                                ? 'bg-red-900/40 border-red-500/50 text-red-400'
                                                : 'bg-[#0a0a0b] border-gray-700 text-gray-400 hover:border-gray-600'
                                                }`}
                                        >
                                            {pos}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Special */}
                            <div>
                                <h5 className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wider">Special Teams</h5>
                                <div className="flex flex-wrap gap-1.5">
                                    {POSITIONS.Special.map(pos => (
                                        <button
                                            key={pos}
                                            onClick={() => togglePosition(pos)}
                                            className={`px-2 py-1 rounded text-xs font-medium border transition-colors ${selectedPositions.includes(pos)
                                                ? 'bg-blue-900/40 border-blue-500/50 text-blue-400'
                                                : 'bg-[#0a0a0b] border-gray-700 text-gray-400 hover:border-gray-600'
                                                }`}
                                        >
                                            {pos}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Captain Toggle */}
                    {!requestMode && (
                        <>
                            <div className="bg-blue-900/20 border border-blue-900/50 p-4 rounded-lg">
                                <div className="flex items-start space-x-3">
                                    <Checkbox
                                        id="is_captain"
                                        checked={isCaptain}
                                        onCheckedChange={setIsCaptain}
                                        className="border-blue-500 data-[state=checked]:bg-blue-600 data-[state=checked]:text-white mt-1"
                                    />
                                    <div className="grid gap-1.5 leading-none">
                                        <Label
                                            htmlFor="is_captain"
                                            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-blue-100"
                                        >
                                            Make Captain
                                        </Label>
                                        <p className="text-xs text-blue-300">
                                            Grants full coach-level access and distinct captain badge.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Posting Rights Toggle */}
                            <div className="bg-purple-900/20 border border-purple-900/50 p-4 rounded-lg">
                                <div className="flex items-start space-x-3">
                                    <Checkbox
                                        id="manage_posts"
                                        checked={permissions.includes('manage_posts') || isCaptain}
                                        onCheckedChange={(checked) => handlePermissionChange('manage_posts', checked)}
                                        disabled={isCaptain}
                                        className="border-purple-500 data-[state=checked]:bg-purple-600 data-[state=checked]:text-white mt-1"
                                    />
                                    <div className="grid gap-1.5 leading-none">
                                        <Label
                                            htmlFor="manage_posts"
                                            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-purple-100 flex items-center gap-2"
                                        >
                                            <Users className="w-4 h-4" />
                                            Allow Posting
                                        </Label>
                                        <p className="text-xs text-purple-300">
                                            Allows member to share updates, images, and videos.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-4">
                                <h4 className="text-sm font-medium text-gray-400">Specific Permissions</h4>
                                {PERMISSIONS_CONFIG.map((perm) => (
                                    <div key={perm.id} className="flex items-start space-x-3">
                                        <Checkbox
                                            id={perm.id}
                                            checked={permissions.includes(perm.id) || isCaptain}
                                            onCheckedChange={(checked) => handlePermissionChange(perm.id, checked)}
                                            disabled={isCaptain} // Captain has all permissions implicitly or explicitly
                                            className="border-gray-600 data-[state=checked]:bg-purple-600 data-[state=checked]:text-white mt-1"
                                        />
                                        <div className="grid gap-1.5 leading-none">
                                            <Label
                                                htmlFor={perm.id}
                                                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex items-center gap-2"
                                            >
                                                <perm.icon className="w-3 h-3 text-gray-400" />
                                                {perm.label}
                                            </Label>
                                            <p className="text-xs text-gray-500">
                                                {perm.description}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>

                <DialogFooter>
                    <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-gray-400 hover:text-white">
                        Cancel
                    </Button>
                    <Button onClick={handleSave} disabled={loading} className="bg-blue-600 hover:bg-blue-700 text-white">
                        {loading ? 'Saving...' : (requestMode ? 'Submit Request' : 'Save Changes')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default PermissionsModal;
